import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, ChevronRight, CircleDashed, Loader2, Plus, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { FormSection } from '@/components/forms/Form';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect } from '@/components/forms/FormField';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useComptesBancaires } from '@/hooks/queries/useBanques';
import { useOperations } from '@/hooks/queries/useOperations';
import {
  useCreateRapprochement,
  useInsertRapprochementLignes,
  useRapprochementLignes,
  useRapprochements,
  useUpdateRapprochementLigne,
  useUpdateRapprochementStatus,
} from '@/hooks/queries/useRapprochements';
import { useRole } from '@/hooks/useRole';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type { RapprochementLigneRow, RapprochementRow } from '@/lib/database.types';

const LIGNE_VARIANT: Record<
  RapprochementLigneRow['statut'],
  'success' | 'warning' | 'destructive'
> = {
  rapprochee: 'success',
  partielle: 'warning',
  non_rapprochee_releve: 'destructive',
  non_rapprochee_erp: 'destructive',
};

export function RapprochementPage(): JSX.Element {
  const { isAdmin } = useRole();
  const canView = usePermission('rapprochement.view');
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: rapprochements = [] } = useRapprochements(entrepriseId);
  const [selected, setSelected] = useState<RapprochementRow | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!canView) return <AccessDenied feature="rapprochement.view" />;

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t('finances.rapprochement.title')}
        subtitle={t('finances.rapprochement.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('finances.rapprochement.newReconciliation')}
            </Button>
          </div>
        }
      />

      {selected ? (
        <RapprochementDetail rapp={selected} onBack={() => setSelected(null)} canEdit={isAdmin} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Mois</th>
                <th className="px-3 py-2 text-left">Compte</th>
                <th className="px-3 py-2 text-right">Total relevé</th>
                <th className="px-3 py-2 text-right">Total ERP</th>
                <th className="px-3 py-2 text-right">Écart</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-right">Détail</th>
              </tr>
            </thead>
            <tbody>
              {rapprochements.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                    Aucun rapprochement créé.
                  </td>
                </tr>
              ) : (
                rapprochements.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-3 py-2 text-xs">{formatDateDZ(r.mois).slice(3)}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">
                      {r.compte_bancaire_id.slice(0, 8)}…
                    </td>
                    <td className="num px-3 py-2 text-right text-xs">
                      {formatDA(r.releve_total_credit - r.releve_total_debit)}
                    </td>
                    <td className="num px-3 py-2 text-right text-xs">
                      {formatDA(r.erp_total_credit - r.erp_total_debit)}
                    </td>
                    <td
                      className={cn(
                        'num px-3 py-2 text-right text-xs font-medium',
                        Math.abs(r.ecart) < 0.01 && 'text-success',
                        Math.abs(r.ecart) >= 0.01 && 'text-destructive',
                      )}
                    >
                      {formatDA(r.ecart)}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={r.statut === 'valide' ? 'success' : 'warning'}>
                        {r.statut}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setSelected(r)}>
                        Voir <ChevronRight className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <NewRapprochementDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entrepriseId={entrepriseId}
      />
    </div>
  );
}

function RapprochementDetail({
  rapp,
  onBack,
  canEdit,
}: {
  rapp: RapprochementRow;
  onBack: () => void;
  canEdit: boolean;
}): JSX.Element {
  const { data: lignes = [], isLoading } = useRapprochementLignes(rapp.id);
  const updateLigne = useUpdateRapprochementLigne();
  const updateStatus = useUpdateRapprochementStatus();

  const counts = useMemo(() => {
    return {
      total: lignes.length,
      rapprochee: lignes.filter((l) => l.statut === 'rapprochee').length,
      partielle: lignes.filter((l) => l.statut === 'partielle').length,
      manquantes: lignes.filter(
        (l) => l.statut === 'non_rapprochee_releve' || l.statut === 'non_rapprochee_erp',
      ).length,
    };
  }, [lignes]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={onBack}>
          ← Retour
        </Button>
        {canEdit && rapp.statut === 'en_cours' && (
          <Button
            onClick={async () => {
              await updateStatus.mutateAsync({ id: rapp.id, statut: 'valide' });
              toast.success('Rapprochement validé');
              onBack();
            }}
          >
            <CheckCircle2 className="h-4 w-4" /> Valider le rapprochement
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <SmallStat label="Total lignes" value={counts.total} tone="default" />
        <SmallStat label="Rapprochées" value={counts.rapprochee} tone="success" />
        <SmallStat label="Partielles" value={counts.partielle} tone="warning" />
        <SmallStat label="Non rapprochées" value={counts.manquantes} tone="destructive" />
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Date relevé</th>
                <th className="px-3 py-2 text-left">Libellé relevé</th>
                <th className="px-3 py-2 text-right">Montant relevé</th>
                <th className="px-3 py-2 text-left">Op. ERP</th>
                <th className="px-3 py-2 text-right">Montant ERP</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                  <td className="px-3 py-2 text-xs">
                    {l.releve_date ? formatDateDZ(l.releve_date) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">{l.releve_libelle ?? '—'}</td>
                  <td className="num px-3 py-2 text-right text-xs">
                    {l.releve_montant ? formatDA(l.releve_montant) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {l.operation_id ? (
                      <span className="font-mono">{l.operation_id.slice(0, 8)}…</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="num px-3 py-2 text-right text-xs">
                    {l.erp_montant ? formatDA(l.erp_montant) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={LIGNE_VARIANT[l.statut]}>
                      {t(`finances.rapprochement.ligneStatut.${l.statut}`)}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && l.statut !== 'rapprochee' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={async () => {
                          await updateLigne.mutateAsync({
                            id: l.id,
                            statut: 'rapprochee',
                            rapprochee_manuellement: true,
                          });
                          toast.success('Ligne marquée rapprochée');
                        }}
                      >
                        <CircleDashed className="h-3 w-3" />{' '}
                        {t('finances.rapprochement.matchManual')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {lignes.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                    Aucune ligne. Importez le relevé bancaire pour démarrer.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewRapprochementDialog({
  open,
  onOpenChange,
  entrepriseId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
}): JSX.Element {
  const create = useCreateRapprochement();
  const insertLignes = useInsertRapprochementLignes();
  const { data: comptes = [] } = useComptesBancaires(entrepriseId);
  const [compteId, setCompteId] = useState('');
  const [mois, setMois] = useState(() => new Date().toISOString().slice(0, 7) + '-01');
  const [file, setFile] = useState<File | null>(null);
  const { data: opsData } = useOperations({
    entrepriseId,
    compteBancaireId: compteId || null,
    dateFrom: mois,
    dateTo: mois.slice(0, 8) + '31',
    pageSize: 500,
  });
  const ops = opsData?.rows ?? [];

  useEffect(() => {
    if (open && comptes[0]) setCompteId(comptes[0].id);
  }, [open, comptes]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t('finances.rapprochement.newReconciliation')}
      description="Crée un rapprochement vide ou importe le relevé bancaire (XLSX)."
      isSubmitting={create.isPending || insertLignes.isPending}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!compteId) {
          toast.error('Choisissez un compte');
          return;
        }
        const rapp = await create.mutateAsync({
          entreprise_id: entrepriseId,
          compte_bancaire_id: compteId,
          mois,
        });

        // Si fichier importé : parse XLSX dynamiquement
        if (file) {
          const xlsx = await import('xlsx');
          const buf = await file.arrayBuffer();
          const wb = xlsx.read(buf, { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0] ?? ''];
          if (!sheet) {
            toast.error('Fichier illisible');
            onOpenChange(false);
            return;
          }
          const json = xlsx.utils.sheet_to_json<Record<string, unknown>>(sheet, {
            defval: '',
            raw: false,
          });
          const lignes = json.map((row) => {
            const date = String(row['Date'] ?? row['date'] ?? row['DATE'] ?? '').trim();
            const libelle = String(
              row['Libellé'] ?? row['Libelle'] ?? row['libelle'] ?? row['Description'] ?? '',
            ).trim();
            const montant = Number(
              String(row['Montant'] ?? row['montant'] ?? row['MONTANT'] ?? '0')
                .replace(/[\s,]/g, (m) => (m === ',' ? '.' : ''))
                .replace(/[^\d.-]/g, ''),
            );
            const sens: 'entree' | 'sortie' = montant >= 0 ? 'entree' : 'sortie';
            // Tente un match auto par montant + date proche
            const candidate = ops.find(
              (o) =>
                Math.abs(Number(o.montant_dzd) - Math.abs(montant)) < 0.5 &&
                date &&
                o.date_operation.slice(0, 10) === isoDate(date),
            );
            return {
              rapprochement_id: rapp.id,
              releve_date: isoDate(date) || null,
              releve_libelle: libelle,
              releve_montant: Math.abs(montant),
              releve_sens: sens,
              operation_id: candidate?.id ?? null,
              statut: candidate ? ('rapprochee' as const) : ('non_rapprochee_releve' as const),
              erp_montant: candidate ? Number(candidate.montant_dzd) : null,
              erp_date: candidate ? candidate.date_operation.slice(0, 10) : null,
              erp_libelle: candidate ? candidate.motif : null,
            };
          });
          if (lignes.length > 0) {
            await insertLignes.mutateAsync(lignes);
          }
          toast.success(`${lignes.length} lignes du relevé importées`);
        } else {
          toast.success('Rapprochement créé (vide)');
        }
        onOpenChange(false);
      }}
    >
      <FormSection>
        <FormSelect
          label="Compte bancaire"
          required
          value={compteId}
          onChange={setCompteId}
          options={[
            { value: '', label: '— Choisir —' },
            ...comptes.map((c) => ({ value: c.id, label: c.intitule })),
          ]}
          fullWidth
        />
        <FormField
          type="month"
          label="Mois"
          required
          value={mois.slice(0, 7)}
          onChange={(e) => setMois(e.target.value + '-01')}
        />
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-sm font-medium">{t('finances.rapprochement.uploadReleve')}</label>
          <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 text-sm hover:bg-secondary">
            <Upload className="h-4 w-4" />
            {file ? file.name : 'Choisir un fichier (.xlsx, .csv)'}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setFile(f ?? null);
                e.target.value = '';
              }}
            />
          </label>
          <p className="text-xs text-muted-foreground">
            Colonnes attendues : Date, Libellé, Montant (négatif = débit). Auto-matching sur montant
            + date.
          </p>
        </div>
      </FormSection>
    </FormDialog>
  );
}

function SmallStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'default' | 'success' | 'warning' | 'destructive';
}): JSX.Element {
  const cls = {
    default: 'text-foreground',
    success: 'text-success',
    warning: 'text-warning',
    destructive: 'text-destructive',
  } as const;
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('num text-2xl font-semibold', cls[tone])}>{value}</div>
    </div>
  );
}

function isoDate(s: string): string {
  // accepte DD/MM/YYYY ou YYYY-MM-DD ou DD-MM-YYYY
  const m1 = /^(\d{2})[/-](\d{2})[/-](\d{4})$/.exec(s);
  if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
  const m2 = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
  return '';
}
